<template>
  <v-sheet class="user-menu-sheet">
    <div v-if="user" class="user-menu-context">
      <div class="user-header" :class="headerClass">
        <MemberAvatar
          :is-limited-platform="isLimitedPlatform"
          :is-user-info-skeleton-shown="isUserInfoSkeletonShown"
          :avatar-size="props.avatarSize"
          :avatar-class="props.avatarClass"
        />

        <div class="wd-overflow-hidden wd-flex-1" :class="props.userInfoClass">
          <MemberNickname
            :is-user-info-skeleton-shown="isUserInfoSkeletonShown"
            :class="[props.isNavbar ? 'wd-order-0' : 'wd-order-1', props.nicknameClass]"
          />
          <MemberLevel
            v-if="!isFunBookV2"
            :is-user-info-skeleton-shown="isUserInfoSkeletonShown"
            :membership-level-icon="membershipLevelIcon"
            :membership-level-name="membershipLevelName"
            :membership-level-color-class="membershipLevelColorClass"
            :membership-level-name-class="props.membershipLevelNameClass"
            :class="props.isNavbar ? 'wd-order-1' : 'wd-order-0'"
          />
        </div>

        <v-btn
          v-if="isCloseBtnShown"
          :class="props.closeBtnClass"
          min-width="40"
          width="40"
          height="40"
          icon
          variant="text"
          @click.stop="$emit('close')"
        >
          <funnow-icon max-width="24" height="24" image-path="cancel-icon_black.svg" />
        </v-btn>
      </div>

      <div class="menu-content">
        <v-list v-if="!isFunBookV2" class="member-assets wd-py-0" :class="props.memberAssetsClass">
          <v-list-item
            v-for="asset in memberAssets"
            :key="asset.path"
            class="menu-content__list"
            v-bind="asset.anchorData"
            @click.stop="$emit('item-click')"
          >
            <v-list-item-title>
              <div class="wd-flex wd-justify-between">
                <div class="wd-flex wd-items-center wd-mr-2">
                  <funnow-icon class="wd-mr-2" active-class="wd-h-6 wd-w-6" :image-path="asset.iconFilename" />
                  <span class="wd-text-omd">{{ asset.text }}</span>
                </div>

                <div v-if="asset.amount !== undefined" class="wd-text-omd">{{ asset.amount }}</div>
              </div>
            </v-list-item-title>
          </v-list-item>
        </v-list>

        <v-list class="wd-py-0">
          <template v-for="action in memberLinks" :key="action.path">
            <v-list-item
              v-if="action.show"
              v-slot="{ isActive }"
              class="menu-content__list"
              v-bind="action.anchorData"
              @click.stop="$emit('item-click')"
            >
              <v-list-item-title>
                <div class="wd-flex wd-items-center wd-justify-between wd-gap-2">
                  <div class="wd-flex wd-items-center wd-gap-2">
                    <funnow-icon active-class="wd-h-6 wd-w-6" :image-path="action.iconFilename" />
                    <span class="wd-text-omd">{{ action.text }}</span>
                  </div>
                  <div class="wd-flex wd-items-center wd-gap-2">
                    <span
                      v-if="action.showIncompleteHint && !isActive"
                      class="wd-text-otempmin wd-text-red70 wd-bg-red10 wd-rounded wd-px-2 wd-py-0.5"
                    >
                      {{ $t('member_profile_not_complete_hint') }}
                    </span>
                    <funnow-icon v-if="isNavbar" active-class="wd-h-5 wd-w-5 wd--mr-2" image-path="chevron-right.svg" />
                  </div>
                </div>
              </v-list-item-title>
            </v-list-item>
          </template>
          <!-- Language Row — hidden on md+ -->
          <div class="md:wd-hidden">
            <MenuLanguageAndDownload @language-changed="$emit('close')" />
          </div>
        </v-list>
        <v-list v-if="showLogout" class="logOutList wd-py-0">
          <v-list-item class="menu-content__list" @click="logout">
            <span class="wd-text-omd wd-block">{{ $t('personal_logout') }}</span>
          </v-list-item>
        </v-list>
      </div>
    </div>
  </v-sheet>
</template>

<script lang="ts" setup>
import { useFunBookV2 } from '@/composition/useFunbookV2'
import { useIntlNumber } from '@/composition/useIntl'
import { usePlatformStore } from '@/store-pinia/platform'
import { useMembershipLevel, useUserInfo } from '@/composition/useMember'

import FunnowIcon from '@/components/icons/funnow-icon.vue'
import MemberAvatar from '@/components/member/MemberAvatar.vue'
import MemberNickname from '@/components/member/MemberNickname.vue'
import MemberLevel from '@/components/member/MemberLevel.vue'
import MenuLanguageAndDownload from '@/components/navigation/MenuLanguageAndDownload.vue'

interface Props {
  isNavbar: boolean
  isCloseBtnShown?: boolean
  avatarSize?: number | string
  headerClass?: ClassType
  userInfoClass?: ClassType
  nicknameClass?: ClassType
  membershipLevelNameClass?: ClassType
  avatarClass?: ClassType
  closeBtnClass?: ClassType
  memberAssetsClass?: ClassType
  showLogout?: boolean
}

interface Emits {
  (e: 'item-click'): void
  (e: 'close'): void
}

const MAX_ASSET_DISPLAY = 999999

const props = withDefaults(defineProps<Props>(), {
  showLogout: true,
})
const emit = defineEmits<Emits>()

const platformStore = usePlatformStore()
const { isFunBookV2 } = storeToRefs(platformStore)

const { $store: store } = useNuxtApp()
const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const localePath = useLocalePath()
const { branchId } = useFunBookV2()
const { getLocaleNumber } = useIntlNumber()
const { isProfileIncomplete } = useUserInfo()

const user = computed(() => store.getters['authentication/getUser'] || {})
const isUserInfoLoading = computed(() => store.getters['authentication/getIsUserInfoLoading'])
const isBookingEngine = computed(() => store.getters.isBookingEngine)
const isUserInfoSkeletonShown = computed(() => isUserInfoLoading.value && !store.getters['authentication/getUser'])
const isLimitedPlatform = computed(() => isBookingEngine.value || isFunBookV2.value)

const { membershipLevelColorClass, linearGradientClass, membershipLevelIcon, membershipLevelName } = useMembershipLevel(user)

const headerClass = computed(() => [props.headerClass, !props.isNavbar && !isFunBookV2.value && linearGradientClass.value])
const funCoinsAmountText = computed(() => formatAssetAmount(user.value.bonus))
const funMilesAmountText = computed(() => formatAssetAmount(user.value.benefit?.mile))

const memberAssets = computed(() => {
  return [
    {
      path: '/member/fun-coin',
      text: t('all_fun_coin'),
      iconFilename: 'fun_coins.svg',
      amount: funCoinsAmountText.value,
    },
    {
      path: '/member/funmiles',
      text: t('miles_page_title'),
      iconFilename: 'funmiles.svg',
      amount: funMilesAmountText.value,
    },
  ].map(assetData => ({
    ...assetData,
    anchorData: assetData.path.startsWith('/') ? { as: 'nuxt-link', to: localePath({ path: assetData.path }) } : { as: 'a', href: assetData.path },
  }))
})
const memberLinks = computed(() => {
  return [
    {
      path: isFunBookV2.value ? `/funbook/${branchId.value}/orders/list` : '/orders/list',
      text: t('my_bookings_title'),
      iconFilename: 'order.svg',
      show: true,
    },
    { path: '/member/promo-code', text: t('my_promo_title'), iconFilename: 'promo_code.svg', show: !isLimitedPlatform.value },
    { path: '/gift/list', text: t('my_gift_box'), iconFilename: 'gift.svg', show: !isLimitedPlatform.value },
    {
      path: '/member/personal-info',
      text: t('personal_information_title'),
      iconFilename: 'member-icon.svg',
      show: !isLimitedPlatform.value,
      showIncompleteHint: isProfileIncomplete.value,
    },
  ].map(linkData => ({
    ...linkData,
    anchorData: linkData.path.startsWith('/') ? { as: 'nuxt-link', to: localePath({ path: linkData.path }) } : { as: 'a', href: linkData.path },
  }))
})

function formatAssetAmount(value?: number) {
  const _value = value || 0

  return _value > MAX_ASSET_DISPLAY ? '1M+' : getLocaleNumber(_value)
}

async function logout() {
  try {
    await store.dispatch('authentication/logout')
    store.dispatch('setUserNavigation', false)

    if (isFunBookV2.value) {
      const path = localePath(`/funbook/${branchId.value}/branch`)
      location.replace(`${path}${location.search}`)
      return
    }
    if (isBookingEngine.value) {
      router.replace({ path: localePath('/'), query: route.query })
      return
    }
    location.replace('/')
  } catch (error) {
    store.dispatch('setUserNavigation', false)
    console.error(error)
  }
}
</script>

<style lang="scss" scoped>
.user-menu-sheet {
  .menu-content > div {
    border-top: 1px solid $gray40;
  }
  .menu-content__list {
    color: $black50 !important;
    @apply wd-px-5 wd-py-4;
  }
}

:deep(.v-list-item) {
  .v-list-item__overlay {
    @apply wd-bg-transparent;
  }
}
</style>
